import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, ShieldCheck, Building2, Store } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0A0A0A]">
      {/* HEADER */}
      <header className="backdrop-blur-xl bg-[#F9F9F8]/85 border-b border-[#E4E4E7] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-baseline gap-1">
            <span className="font-serif text-3xl font-light tracking-tighter">Forge</span>
            <span className="text-[#E63946] text-xl leading-none">.</span>
          </Link>
          <nav className="hidden md:flex items-center gap-10 text-sm font-display">
            <a href="#platform" className="hover:text-[#E63946] transition-colors">Platform</a>
            <a href="#training" className="hover:text-[#E63946] transition-colors">For healthcare teams</a>
            <a href="#ai" className="hover:text-[#E63946] transition-colors">AI Studio</a>
            <a href="#pricing" className="hover:text-[#E63946] transition-colors">Why Forge</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="header-login" className="text-sm font-display hover:text-[#E63946] transition-colors">Sign in</Link>
            <a href="https://hippomt.com/schedule" target="_blank" rel="noreferrer" data-testid="header-signup" className="btn-primary text-xs">Schedule a call →</a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-8 pt-20 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 fade-in">
            <div className="overline mb-8">Continuing education for healthcare · est. 2026</div>
            <h1 className="editorial-h1 text-5xl sm:text-6xl lg:text-[7rem]">
              Training that<br/>
              keeps your<br/>
              <span className="text-[#E63946]">team</span> confident,<br/>
              capable, current.
            </h1>
            <p className="mt-10 text-lg max-w-xl text-[#0A0A0A]/75 leading-relaxed">
              Forge is a modern learning platform for healthcare organizations — one place to deliver clinical training, run testing, and issue certifications across every employee, every shift, every site.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="https://hippomt.com/schedule" target="_blank" rel="noreferrer" data-testid="hero-cta-primary" className="btn-primary">
                Schedule a call <ArrowUpRight size={16} strokeWidth={1.5} />
              </a>
              <Link to="/login" data-testid="hero-cta-secondary" className="btn-secondary">
                Sign in to your company portal
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <div><div className="font-serif text-3xl font-light">100%</div><div className="overline mt-1">Tracked completion</div></div>
              <div><div className="font-serif text-3xl font-light">24/7</div><div className="overline mt-1">Learner access</div></div>
              <div><div className="font-serif text-3xl font-light">HIPAA</div><div className="overline mt-1">Aware by design</div></div>
            </div>
          </div>
          <div className="lg:col-span-5 fade-in-delay-2">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1666886573301-b5d526cfd518?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Healthcare professionals in training"
                className="w-full h-[560px] object-cover border border-[#0A0A0A]"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0A0A0A] text-white p-5 max-w-xs border border-[#0A0A0A]">
                <div className="overline text-[#E63946]">In production</div>
                <div className="font-serif text-2xl mt-2 leading-tight">412 clinicians completed annual compliance training this quarter — without a single email reminder.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-10 border-y border-[#E4E4E7] overflow-hidden bg-white">
        <div className="text-center overline mb-6 text-[#52525B]">Training Categories on Forge</div>
        <div className="marquee-wrapper">
          <div className="marquee-track">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="marquee-content font-serif text-3xl text-[#52525B]" aria-hidden={i === 1 ? "true" : undefined}>
                <span>OSHA Bloodborne Pathology</span>
                <span>·</span>
                <span className="text-[#E63946]">Code of Conduct</span>
                <span>·</span>
                <span>Ethics</span>
                <span>·</span>
                <span>HIPAA and Security</span>
                <span>·</span>
                <span className="text-[#E63946]">General Compliance</span>
                <span>·</span>
                <span>Fraud, Waste and Abuse Prevention</span>
                <span>·</span>
                <span>Abuse, Neglect and Exploitation</span>
                <span>·</span>
                <span className="text-[#E63946]">Board of Directors</span>
                <span>·</span>
                <span>EMTALA</span>
                <span>·</span>
                <span>Chief Compliance Training</span>
                <span>·</span>
                <span className="text-[#E63946]">Anti-Violence</span>
                <span>·</span>
                <span>Sexual Harassment Prevention</span>
                <span>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section id="platform" className="max-w-7xl mx-auto px-8 py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <div className="overline mb-4">The platform</div>
            <h2 className="editorial-h2 text-4xl sm:text-5xl">Built for the way healthcare actually trains.</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 text-[#52525B] text-base leading-relaxed self-end">
            {"From new-hire orientation to annual compliance to specialty certifications — every course, every learner, every record lives in one calm, audit-ready system. Designed with clinical educators, compliance officers, and HR leaders in mind."}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Large feature */}
          <div className="md:col-span-7 bg-[#0A0A0A] text-white p-10 min-h-[360px] flex flex-col justify-between" data-testid="feature-multitenant">
            <div className="overline text-[#E63946]">01 · One portal per company</div>
            <div>
              <h3 className="font-serif text-4xl font-light leading-tight">Every facility gets its own secure learning portal.</h3>
              <p className="mt-4 text-zinc-400 max-w-md">Branded sign-in, isolated learner records, admin dashboards. Whether you run one clinic or a network of fifty, your data stays cleanly separated.</p>
            </div>
          </div>

          <div className="md:col-span-5 editorial-card-static p-10 min-h-[360px] flex flex-col justify-between" data-testid="feature-marketplace">
            <Store size={28} strokeWidth={1.5} />
            <div>
              <div className="overline mb-3">02 · A library of clinical training</div>
              <h3 className="font-serif text-3xl font-light leading-tight">Ready-to-deploy courses your team can start today.</h3>
              <p className="mt-3 text-[#52525B] text-sm">Browse vetted training, license seats for your employees, and roll them out with a single shareable link.</p>
            </div>
          </div>

          <div className="md:col-span-4 editorial-card-static p-10 min-h-[280px] flex flex-col justify-between" data-testid="feature-custom">
            <Building2 size={28} strokeWidth={1.5} />
            <div>
              <div className="overline mb-3">03 · Custom programs</div>
              <h3 className="font-serif text-2xl font-light leading-tight">{"Training built specifically for your facility's protocols."}</h3>
            </div>
          </div>

          <div className="md:col-span-4 bg-white p-10 min-h-[280px] flex flex-col justify-between border border-[#0A0A0A]" data-testid="feature-ai">
            <Sparkles size={28} strokeWidth={1.5} className="text-[#E63946]" />
            <div>
              <div className="overline mb-3 text-[#E63946]">04 · Testing & coaching</div>
              <h3 className="font-serif text-2xl font-light leading-tight">Knowledge checks, quiz scoring, and a 24/7 learner assistant.</h3>
            </div>
          </div>

          <div className="md:col-span-4 editorial-card-static p-10 min-h-[280px] flex flex-col justify-between" data-testid="feature-cert">
            <ShieldCheck size={28} strokeWidth={1.5} />
            <div>
              <div className="overline mb-3">05 · Certifications, on file</div>
              <h3 className="font-serif text-2xl font-light leading-tight">{"Auto-issued certificates with verifiable numbers — ready for surveyors."}</h3>
            </div>
          </div>
        </div>
      </section>

      {/* FOR COMPANIES */}
      <section id="training" className="bg-white border-y border-[#E4E4E7] py-28">
        <div className="max-w-7xl mx-auto px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img src="https://images.unsplash.com/photo-1666214280557-f1b5022eb634?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Clinical training" className="w-full h-[500px] object-cover border border-[#0A0A0A]" />
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="overline mb-4">For healthcare companies</div>
            <h2 className="editorial-h2 text-4xl sm:text-5xl">Log in. Assign training. Watch every team member finish.</h2>
            <p className="mt-6 text-[#52525B] leading-relaxed max-w-lg">
              {"Your company gets its own login. Distribute training to every employee with a single link. Track who's completed what, view quiz scores, download certificates, and export a full audit report — anytime a regulator, surveyor, or board member asks."}
            </p>
            <ul className="mt-8 space-y-3 text-sm text-[#0A0A0A] max-w-md">
              <li className="flex items-start gap-3"><span className="text-[#E63946] font-bold">→</span>Per-employee completion tracking with timestamps</li>
              <li className="flex items-start gap-3"><span className="text-[#E63946] font-bold">→</span>Quiz scores and overall company score at a glance</li>
              <li className="flex items-start gap-3"><span className="text-[#E63946] font-bold">→</span>Certificates archived and exportable as CSV or PDF</li>
              <li className="flex items-start gap-3"><span className="text-[#E63946] font-bold">→</span>{"Custom training tailored to your facility's needs"}</li>
            </ul>
            <Link to="/signup" data-testid="companies-cta" className="btn-primary mt-10">Request a company portal <ArrowUpRight size={16} strokeWidth={1.5}/></Link>
          </div>
        </div>
      </section>

      {/* AI SECTION */}
      <section id="ai" className="bg-[#0A0A0A] text-white py-28">
        <div className="max-w-7xl mx-auto px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="overline text-[#E63946] mb-6">Why Forge</div>
            <h2 className="editorial-h2 text-4xl sm:text-5xl text-white">A learning platform that respects clinical time.</h2>
            <p className="mt-8 text-zinc-400 max-w-lg">
              {"We've designed Forge specifically for healthcare: short, focused lessons that fit between shifts; testing that actually validates understanding; certifications that hold up under audit. And an AI assistant that helps your team — never replaces them."}
            </p>
            <a href="https://hippomt.com/schedule" target="_blank" rel="noreferrer" data-testid="ai-cta" className="btn-primary bg-[#E63946] border-[#E63946] hover:bg-[#D62828] mt-10">
              Talk to our team <ArrowUpRight size={16} strokeWidth={1.5} />
            </a>
          </div>
          <div className="md:col-span-6">
            <div className="bg-zinc-900 border border-zinc-800 p-8 font-mono-jb text-sm">
              <div className="text-zinc-500 mb-4 overline text-[#E63946]">Forge // Sample curriculum</div>
              <div className="text-zinc-300">→ program: <span className="text-white">{'"Infection control · annual refresher"'}</span></div>
              <div className="text-zinc-300 mt-2">→ length: <span className="text-white">6 lessons · ~45 min · 1 test</span></div>
              <div className="text-zinc-500 mt-6">Module 1 · Hand hygiene fundamentals</div>
              <div className="text-zinc-500">Module 2 · PPE selection & use</div>
              <div className="text-zinc-500">Module 3 · Bloodborne pathogen exposure</div>
              <div className="text-zinc-500">Module 4 · Isolation precautions</div>
              <div className="text-zinc-500">Module 5 · Reporting & documentation</div>
              <div className="text-zinc-500">Module 6 · Knowledge check (passing 85%)</div>
              <div className="text-[#E63946] mt-6">✓ certificate auto-issued on pass</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-8 py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline mb-4">Get in touch</div>
            <h2 className="editorial-h2 text-5xl sm:text-6xl">Ready to bring your training onto Forge?</h2>
            <p className="mt-6 text-[#52525B] max-w-lg">{"Whether you have one course or fifty, one facility or fifty — we'll help you get it live, get your team enrolled, and get the documentation your organization needs."}</p>
            <div className="mt-10 flex gap-4 flex-wrap">
              <a href="https://hippomt.com/schedule" target="_blank" rel="noreferrer" data-testid="footer-cta-signup" className="btn-primary">Schedule a call</a>
              <Link to="/login" data-testid="footer-cta-login" className="btn-secondary">Sign in to your portal</Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <img src="https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" className="w-full h-[420px] object-cover border border-[#0A0A0A]" alt="Healthcare team training" />
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E4E4E7] py-10">
        <div className="max-w-7xl mx-auto px-8 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-baseline gap-1">
            <span className="font-serif text-2xl font-light tracking-tighter">Forge</span>
            <span className="text-[#E63946]">.</span>
          </div>
          <div className="overline text-[#52525B]">© 2026 Forge · A modern learning platform for healthcare</div>
        </div>
      </footer>
    </div>
  );
}
